import { BCMSDate, BCMSRecord, BCMSStateManagerCRUD, DisabledPrompt, StateManager, useBCMSContext, useModel, useStateManager } from "@bcm-dev/bcms-vite";
import CustomSelect, { CustomSelectItem } from 'components/custom-ui/CustomSelect';
import DateSelect from 'components/custom-ui/DateSelect';
import { FormContainer, FormRowView, TableBody, TableColumnContainer, TableColumnView, TableItemContainer, TableItemTextView, TableItemView, TableView, WidgetContainer, WidgetView } from 'components/shared';
import { FormButtonRow } from 'components/shared/containers/FormContainer';
import { PrimaryButton, UICheckbox, UITextInput } from 'components/ui';
import Classes, { Account, WireHistory } from "model";
import React from 'react';
import { SVGAsset } from 'res';
import NoWireTransferTableRow from '../NoWireTransferTableRow';
import WireSearch from "./classes/WireSearch";
import "./styles.css";
import "./mobile.css";


/**
 * @function WireHistoryWidget
 * @return {React.Component}
 * @exported true
 * @description The Widget that shows the wire history
 */

export default function WireHistoryWidget(props) {
  const globalController = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, globalController);

  const data = { ...getDefaultData(), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };

  const controller = (state, setState) => ({
      data: state.data,
      ui: state.ui,

      onChange: prop => value => {
        const crud = BCMSStateManagerCRUD.init(state);

        if(prop === "showSearchOptions") {
          const updatedVal = { isActive: value };
          crud.update([ "data", "showSearchOptions" ], updatedVal);

          const modifyPageDataFromWidget = props.action.modifyPageDataFromWidget("wireHistoryWidgetData", "showSearchOptions");
          modifyPageDataFromWidget(updatedVal);
        } else {
          crud.update([ "data", prop ], value);
        }
              
        crud.submit(setState);
      },

      onSearchChange: prop => value => {
        BCMSStateManagerCRUD.init(state)
        .update([ "data", "search", prop ], value)
        .submit(setState);
      },

      // Updates the date ranges in the "From" and "To" fields.
      onChangeDateRange: value => {
        const _value = value.toLowerCase();
        const stateManagerCRUD = BCMSStateManagerCRUD.init(state);
        switch(_value) {
          case "custom":
            stateManagerCRUD
              .update([ "data", "search", "fromDate" ], BCMSDate.init().offset(-7))
              .update([ "data", "search", "toDate" ], undefined)
            break;
          case "today":
            stateManagerCRUD
              .update([ "data", "search", "fromDate" ], BCMSDate.init())
              .update([ "data", "search", "toDate" ], BCMSDate.init())
            break;
          case "yesterday":
            stateManagerCRUD
              .update([ "data", "search", "fromDate" ], BCMSDate.init().offset(-1))
              .update([ "data", "search", "toDate" ], BCMSDate.init().offset(-1))
            break;
          case "this week":
            var firstDayOfWeek = new Date();
            firstDayOfWeek.setDate(firstDayOfWeek.getDate() - firstDayOfWeek.getDay());
              stateManagerCRUD
                .update([ "data", "search", "fromDate" ], BCMSDate.init(firstDayOfWeek))
                .update([ "data", "search", "toDate" ], BCMSDate.init())
              break;
          case "last week":
            var firstDayOfWeek = new Date(); // First day of theweek is this past sunday.
            firstDayOfWeek.setDate(firstDayOfWeek.getDate() - firstDayOfWeek.getDay());
              stateManagerCRUD
                .update([ "data", "search", "fromDate" ], BCMSDate.init(firstDayOfWeek).offset(-7)) // sunday
                .update([ "data", "search", "toDate" ],  BCMSDate.init(firstDayOfWeek).offset(-1)) // last saturday
              break;
          case "this month":
              stateManagerCRUD
                .update([ "data", "search", "fromDate" ], BCMSDate.init().setToFirst())
                .update([ "data", "search", "toDate" ],  BCMSDate.init())
              break;
          case "last month":
            const firstDayLastMonth = BCMSDate.init().setToFirst();
            firstDayLastMonth.offset(-1).setToFirst();
            const lastDayLastMonth = BCMSDate.init().setToFirst();
            lastDayLastMonth.offset(-1);
            stateManagerCRUD
              .update([ "data", "search", "fromDate" ], firstDayLastMonth)
              .update([ "data", "search", "toDate" ], lastDayLastMonth)
            break;
          case "last 3 months":
            const threeMonthsAgo = BCMSDate.init();
            threeMonthsAgo.month = threeMonthsAgo.monthInt - 3;
            stateManagerCRUD
                .update([ "data", "search", "fromDate" ], threeMonthsAgo)
                .update([ "data", "search", "toDate" ], BCMSDate.init())
            break;
          case "last 6 months":
            const sixMonthsAgo = BCMSDate.init();
            sixMonthsAgo.month = sixMonthsAgo.monthInt - 6;
            stateManagerCRUD
                .update([ "data", "search", "fromDate" ], sixMonthsAgo)
                .update([ "data", "search", "toDate" ], BCMSDate.init())
            break;
          case "last 9 months":
            const nineMonthsAgo = BCMSDate.init();
            nineMonthsAgo.month = nineMonthsAgo.monthInt - 9;
            stateManagerCRUD
                .update([ "data", "search", "fromDate" ], nineMonthsAgo)
                .update([ "data", "search", "toDate" ], BCMSDate.init())
            break;
          case "this year":
            const januaryOfThisYear = BCMSDate.init();
            januaryOfThisYear.month = 1;
            januaryOfThisYear.setToFirst();
            stateManagerCRUD
                .update([ "data", "search", "fromDate" ], januaryOfThisYear)
                .update([ "data", "search", "toDate" ], BCMSDate.init())
            break;
          case "last year":
            const januaryOfLastYear = BCMSDate.init();
            januaryOfLastYear.month = 1;
            januaryOfLastYear.setToFirst();
            januaryOfLastYear.offset(-1);
            januaryOfLastYear.month = 1;
            januaryOfLastYear.setToFirst();
            const decemberOfLastYear = BCMSDate.init();
            decemberOfLastYear.month = 1;
            decemberOfLastYear.setToFirst();
            decemberOfLastYear.offset(-1);

            stateManagerCRUD
                .update([ "data", "search", "fromDate" ], januaryOfLastYear)
                .update([ "data", "search", "toDate" ], decemberOfLastYear)
            break;
          case "all dates":
            stateManagerCRUD
                .update([ "data", "search", "fromDate" ], undefined)
                .update([ "data", "search", "toDate" ], undefined)
          default:
            break;
        }
        stateManagerCRUD.update([ "data", "search", "dateRange" ], value)
        stateManagerCRUD.submit(setState);
      }
  });

  return(
    <StateManager state={ { data, ui } } controller={ controller }>
      <WidgetContent/>
    </StateManager>
  )
}

export const getDefaultData = () => ({
  accounts: Account.initAllAsset(),
  showSearchOptions: { isActive: false },
  wireHistory: WireHistory.initAll(),
  search: WireSearch.init()
});

export const getDefaultUI = () => ({
  // no ui changes for now
});

function WidgetContent(props) {
  const { data, onChange, onSearchChange, onChangeDateRange } = useStateManager();
  const { branding } = useModel() ?? {};
  const { options } = branding ?? {};
  const hasNotifyPayeeColumn = options.wire_history_notify_payee_abi22 === "true";
  const noWiresRowColSpan = hasNotifyPayeeColumn ? "9" : "8";
  const hasWireHistory = data.wireHistory.length > 0;

  const search = data.search ?? {};
  const wireHistoryRows = data.wireHistory.filter(item => item.id !== undefined).reduce((acc, history) => {
    acc.push(<WireHistoryTableRow key={ history.id } history={ history } />);
    return acc;
  }, []);

  const mobileWireHistoryRows = data.wireHistory.filter(item => item.id !== undefined).reduce((acc, history) => {
    acc.push(<MobileWireHistoryRow key={ history.id } history={ history } />);
    return acc;
  }, []);

  const accounts = data.accounts.reduce((acc, account, index) => {
    //const label = `${ account.number } - ${ account.nickname } (Available ${ account.availableBalance.format() })`;
    let label = ``;
    if(account.type === "loan") {
      label = `${ account.number } - ${ account.nickname } (Current ${ account.currentBalance.format() })`;
    } else if(account.type === "loc") {
      label = `${ account.number } - ${ account.nickname } (Available ${ account.availableCredit.format() })`;
    } else {
      label = `${ account.number } - ${ account.nickname } (Available ${ account.availableBalance.format() })`;
    }

    if(index === 0) { acc.push(<CustomSelectItem key="allAccounts" value="all accounts">All Accounts</CustomSelectItem>) }
    acc.push(<CustomSelectItem key={ account.id } value={ account.id }>{ label }</CustomSelectItem>);
    return acc;
  }, []);

  return(
    <WidgetContainer className="wire-history-widget">
      <WidgetView text="Wire History">
        <UICheckbox label="Show Search Options" value={ data.showSearchOptions.isActive ? "checked" : "" } onChange={ onChange("showSearchOptions") } />
        { data.showSearchOptions.isActive && 
        <FormContainer className="wire-history-search-options-form small-form-width">
          <FormRowView text="Status">
            <CustomSelect value={ search.status } 
            onChange={ onSearchChange("status") }>
                <CustomSelectItem value="any status">Any Status</CustomSelectItem>
                <CustomSelectItem value="pending approval">Pending Approval</CustomSelectItem>
                <CustomSelectItem value="approval in progress">Approval In Progress</CustomSelectItem>
                <CustomSelectItem value="approved">Approved</CustomSelectItem>
                <CustomSelectItem value="rejected">Rejected</CustomSelectItem>
                <CustomSelectItem value="canceled">Canceled</CustomSelectItem>
                <CustomSelectItem value="canceled by system">Canceled By System</CustomSelectItem>
                <CustomSelectItem value="processing">Processing</CustomSelectItem>
                <CustomSelectItem value="processed">Processed</CustomSelectItem>
                <CustomSelectItem value="processing failed">Processing Failed</CustomSelectItem>
                <CustomSelectItem value="downloaded">Downloaded</CustomSelectItem>
            </CustomSelect>
          </FormRowView>
          <FormRowView text="Funding Account">
            <CustomSelect value={ search.fundingAccountID } onChange={ onSearchChange("fundingAccountID") }>
              { accounts }
            </CustomSelect>
          </FormRowView>
          <FormRowView text="Date Range">
            <CustomSelect value={ search.dateRange } onChange={ onChangeDateRange }>
                <CustomSelectItem value="custom">Custom</CustomSelectItem>
                <CustomSelectItem value="today">Today</CustomSelectItem>
                <CustomSelectItem value="yesterday">Yesterday</CustomSelectItem>
                <CustomSelectItem value="this week">This Week</CustomSelectItem>
                <CustomSelectItem value="last week">Last Week</CustomSelectItem>
                <CustomSelectItem value="this month">This Month</CustomSelectItem>
                <CustomSelectItem value="last month">Last Month</CustomSelectItem>
                <CustomSelectItem value="last 3 months">Last 3 Months</CustomSelectItem>
                <CustomSelectItem value="last 6 months">Last 6 Months</CustomSelectItem>
                <CustomSelectItem value="last 9 months">Last 9 Months</CustomSelectItem>
                <CustomSelectItem value="this year">This Year</CustomSelectItem>
                <CustomSelectItem value="last year">Last Year</CustomSelectItem>
                <CustomSelectItem value="all dates">All Dates</CustomSelectItem>
            </CustomSelect>
          </FormRowView>
          <FormRowView text="From:">
            <DateSelect value={ search.fromDate } enablePast={ true } enableFuture={ false } onChange={ onSearchChange("fromDate") } />
          </FormRowView>
          <FormRowView text="To:">
            <DateSelect value={ search.toDate } onChange={ onSearchChange("toDate") } />
          </FormRowView>
          <FormRowView text="Reference Number">
            <UITextInput value={ search.referenceNumber } onChange={ onSearchChange("referenceNumber") } />
          </FormRowView>
          <FormButtonRow>
            <DisabledPrompt>
              <PrimaryButton text="Search" />
            </DisabledPrompt>
          </FormButtonRow>
        </FormContainer>
        }
        <div className="show-on-desktop">
          <TableView>
            <TableColumnContainer>
              <TableColumnView className="text-left ref-number-text" text="Ref #" />
              <TableColumnView className="text-left payee-text" text="Payee" />
              { hasNotifyPayeeColumn && <TableColumnView className="text-left notify-payee-text" text="Notify Payee" /> }
              <TableColumnView className="text-left funding-account-text" text="Funding Account" />
              <TableColumnView className="text-left amount-text" text="Amount" />
              <TableColumnView className="text-left status-text" text="Status" />
              <TableColumnView className="text-left wire-date-text" text=
                {<>Wire Date
                  <DisabledPrompt>
                    <SVGAsset id="ArrowDownIcon" className="clickable" />
                  </DisabledPrompt>
                </>} 
              />
              <TableColumnView />
            </TableColumnContainer>
            <TableBody>
              { hasWireHistory && wireHistoryRows }
              { !hasWireHistory && <NoWireTransferTableRow colspan="6" /> }
            </TableBody>
          </TableView>
        </div>
        <div className="mobile-wires-history show-on-mobile">
          { mobileWireHistoryRows }
        </div>
        <DisabledPrompt>
          <PrimaryButton text="Export" />
        </DisabledPrompt>
      </WidgetView>
    </WidgetContainer>
  );
}

/**
 * @function WireHistoryTableRow
 * @returns {React.Component}
 * @exported false
 * @description 
 */
function WireHistoryTableRow(props) {
  const history = props.history ?? {};
  const { branding } = useModel() ?? {};
  const { options } = branding ?? {};
  const hasNotifyPayeeColumn = options.wire_history_notify_payee_abi22 === "true";
  const notifyPayee = history.notifyPayee ? "Yes" : "No";
  let status = history.statusDescription;
  // Capitalize first letter of each word.
  // From: https://stackoverflow.com/questions/32589197/how-can-i-capitalize-the-first-letter-of-each-word-in-a-string-using-javascript
  status = status.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.substring(1)).join(' ');
  if(status === "Pending Approval") {
    status = "Pending FI Approval"
  }

  return (
    <TableItemContainer>
      <TableItemTextView text={ history.referenceNumber } />
      <TableItemTextView text={ history.payee.nickname } />
      { hasNotifyPayeeColumn && <TableItemTextView text={ notifyPayee } /> }
      <TableItemTextView text={ history.payee.account } />
      <TableItemTextView text={ history.amount.format() } />
      <TableItemTextView text={ status } />
      <TableItemTextView text={ history.date.format("m/d/Y") } />
      <TableItemView className="wire-history-table-row-icons text-right">
        <DisabledPrompt>
          <SVGAsset id="MagnifyingGlassIcon" className="clickable" />
        </DisabledPrompt>
        <DisabledPrompt>
          <SVGAsset id="EditIcon" className="clickable" />
        </DisabledPrompt>
        <DisabledPrompt>
          <SVGAsset id="TrashcanXIcon" className="clickable" />
        </DisabledPrompt>
      </TableItemView>
    </TableItemContainer>
  );
}

/**
 * @function MobileWireHistoryTableRow
 * @return {React.Component}
 * @exported false
 * @description The row that shows in the MobileWireHistoryTable
 */

function MobileWireHistoryRow(props) {
  const { branding } = useModel() ?? {};
  const { options } = branding ?? {};
  const hasNotifyPayeeColumn = options.wire_history_notify_payee_abi22 === "true";
  const history = props.history ?? {};
  const notifyPayee = history.notifyPayee ? "Yes" : "No";
  let status = history.statusDescription;
  
  // Capitalize first letter of each word.
  status = status.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.substring(1)).join(' ');
  if(status === "Pending Approval") {
    status = "Pending FI Approval";
  }
  
  return(
    <div className="mobile-wire-history-row flex-container flex-horizontal align-center space-between">
      <div className="wire-details-column flex-width">
        <div className="flex-container flex-horizontal space-between wires-item">
          <b className="flex-item set-width">{ history.referenceNumber }</b>
          <p className="set-item flex-width text-right">{ history.amount.format({ style:"decimal" }) }</p>
        </div>
        <div className="flex-container flex-horizontal space-between wires-item">
          <p className="flex-item set-width">Currency:</p>
          <p className="set-item flex-width text-right"></p>
        </div>
        { hasNotifyPayeeColumn
        && <div className="flex-container flex-horizontal space-between wires-item">
            <p className="flex-item set-width">Notify Payee:</p>
            <p className="set-item flex-width text-right">{ notifyPayee }</p>
          </div> 
        }
        <div className="flex-container flex-horizontal space-between wires-item">
          <p className="flex-item set-width">Amount in $:</p>
          <p className="set-item flex-width text-right">{ history.amount.format() }</p>
        </div>
        <div className="flex-container flex-horizontal space-between wires-item">
          <p className="flex-item set-width">Wire Date:</p>
          <p className="set-item flex-width text-right">{ history.date.format('m/d/Y') }</p>
        </div>
        <div className="flex-container flex-horizontal space-between wires-item status">
          <p className="flex-item set-width">Status:</p>
          <p className="set-item flex-width text-right">{ status }</p>
        </div>
      </div>
      <DisabledPrompt><SVGAsset id="MagnifyingGlassIcon" className="inline-text-icon left-icon clickable set-width" /></DisabledPrompt>
      <DisabledPrompt><SVGAsset id="EditIcon" className="inline-text-icon left-icon clickable edit-icon set-width" /></DisabledPrompt>
      <DisabledPrompt><SVGAsset id="TrashcanXIcon" className="inline-text-icon right-icon clickable set-width" /></DisabledPrompt>
    </div>
  );
}